/**
 * Composable for managing SAML SSO authentication flows.
 * Handles initiation of SSO login and redirect to Identity Provider.
 */

interface InitiateSamlFlowResult {
    success: boolean
    loginUrl: string | null
    message: string | null
}

interface SamlProviderTarget {
    providerUid?: string | null
    providerId?: number | null
}

interface ApiError {
    message?: string
    data?: {
        message?: string
    }
}

/**
 * Composable for SAML SSO flow management
 */
export function useSamlFlow() {
    const { cmsRequest } = useGraphQL()

    const isInitiating = ref(false)
    const flowError = ref<string | null>(null)

    /**
     * Initiate SAML authentication flow with a provider
        * Calls the CMS authorised-content login endpoint.
     * 
        * @param gateUid - The page-data gate identifier for the SAML gate
        * @param provider - Provider identifiers accepted by the backend
     * @param returnUrl - The URL to return to after authentication
     * @returns The IdP login URL to redirect to, or null if failed
     */
    const initiateSamlFlow = async (
        gateUid: string,
        provider: SamlProviderTarget,
        returnUrl: string
    ): Promise<string | null> => {
        isInitiating.value = true
        flowError.value = null

        try {
            if (!provider.providerUid && provider.providerId == null) {
                flowError.value = 'SSO provider is missing an identifier'
                return null
            }

            const result = await cmsRequest<InitiateSamlFlowResult>({
                uri: 'authorised-content/saml/login',
                method: 'POST',
                body: {
                    gateUid,
                    returnUrl,
                    ...(provider.providerUid ? { providerUid: provider.providerUid } : {}),
                    ...(provider.providerId != null ? { providerId: provider.providerId } : {}),
                },
            })

            if (result?.success && result.loginUrl) {
                return result.loginUrl
            }

            flowError.value = result?.message || 'Failed to initiate SSO flow'
            return null
        } catch (error) {
            const apiError = error as ApiError
            flowError.value = apiError.data?.message || apiError.message || 'Failed to initiate SSO flow'
            return null
        } finally {
            isInitiating.value = false
        }
    }

    /**
     * Redirect browser to the IdP login URL
        *
     * @param loginUrl - The IdP login URL from initiateSamlFlow
     */
    const redirectToIdp = (loginUrl: string): void => {
        if (!import.meta.client) {
            console.warn('redirectToIdp called on server side, ignoring')
            return
        }

        if (!loginUrl) {
            flowError.value = 'No login URL provided'
            return
        }

        // Perform the redirect
        window.location.href = loginUrl
    }

    return {
        // State
        isInitiating: readonly(isInitiating),
        flowError: readonly(flowError),

        // Methods
        initiateSamlFlow,
        redirectToIdp,
    }
}
